/**
 * LeaveManagement.jsx — 연차 관리 어셈블리
 *
 * 분할 구조:
 *   leavemgmt/constants.js          — 상수 (LEAVE_TYPES_*, BULK_COLUMNS, 뱃지 컬러 등)
 *   leavemgmt/utils.js              — 유틸 함수 (날짜 파싱, 셀 검증, 뱃지 스타일 등)
 *   leavemgmt/stylesA.js            — 스타일 파트A (페이지·헤더·테이블·모바일카드)
 *   leavemgmt/stylesB.js            — 스타일 파트B (뱃지·버튼·폼·모달·시트)
 *   leavemgmt/useLeaveManagement.js — 메인 훅 (상태·API·CRUD 핸들러)
 *   leavemgmt/useLeaveDerived.js    — 파생 데이터 훅 (useMemo 집합)
 *   leavemgmt/useBulkHandlers.js    — 대량 붙여넣기 핸들러 훅
 *   leavemgmt/SummarySection.jsx    — 연차 요약 섹션
 *   leavemgmt/DetailSection.jsx     — 연차 내역 섹션
 *   leavemgmt/AddEditModal.jsx      — 연차 추가/수정 모달
 *   leavemgmt/BulkModal.jsx         — 대량 추가 모달
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLeaveManagement } from './leavemgmt/useLeaveManagement';
import { useLeaveDerived }    from './leavemgmt/useLeaveDerived';
import { useBulkHandlers }    from './leavemgmt/useBulkHandlers';
import { SummarySection }     from './leavemgmt/SummarySection';
import { DetailSection }      from './leavemgmt/DetailSection';
import { AddEditModal }       from './leavemgmt/AddEditModal';
import { EventTypeSettingsModal } from './leavemgmt/EventTypeSettingsModal';
import { BulkModal }          from './leavemgmt/BulkModal';
import CompanyFilterChips     from './integrated/CompanyFilterChips';
import { Users, User, Plus, Settings } from 'lucide-react';
import { HeaderActionBtn } from './attendancemgmt/HeaderActionBtn'; // 옵션 C 헤더 버튼 — 모바일 sticky 헤더 전용
// 데스크탑 헤더는 직원 관리 기준 디자인 표준(designStandard) 사용 (2026-08-26 통일)
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle,
  stdTabGroup, stdTabBtn, stdBlackBtn, stdOutlineBtn,
} from './adminlayout/designStandard';
import { pageWrapStyle, pageHeaderInner, pageTitleStyle } from './leavemgmt/stylesA';
import { inputStyle } from './leavemgmt/stylesB';

// 연차 추가·수정 창의 '유형 템플릿' 목록 (2026-10-06)
//   템플릿은 회사마다 따로다(서버 leave_event_types.company_code). 목록에 회사가 하나뿐이면(본사·회사별 화면) 그대로.
//   여러 회사(통합 메뉴)면 창에서 고른 직원이 모두 한 회사일 때 그 회사 것만 — 아무도 안 골랐거나 회사가 섞이면 비운다
//   (다른 회사 템플릿 번호가 저장되지 않게. 빈 목록이면 EffectFields 가 칸을 숨긴다).
function eventTypesForForm(eventTypes, users, form, selectedUserIds) {
  const list = Array.isArray(eventTypes) ? eventTypes : [];
  const listCompanies = new Set(list.map((t) => String(t.company_code || '').toUpperCase()));
  if (listCompanies.size <= 1) return list;
  const ids = (Array.isArray(selectedUserIds) && selectedUserIds.length > 0)
    ? selectedUserIds
    : (form?.user_id ? [form.user_id] : []);
  const companies = new Set(ids.map((id) => {
    const u = (users || []).find((x) => String(x.id) === String(id));
    return String(u?.company_code || '').toUpperCase();
  }));
  if (companies.size !== 1 || companies.has('')) return [];
  const [code] = [...companies];
  return list.filter((t) => String(t.company_code || '').toUpperCase() === code);
}

function LeaveManagement({
  pageTitle = '연차 관리',
  apiBase = '/api/admin/leaves',
  syncLeavesEndpoint = '/api/admin/sheets/sync/leaves',
  settingsApiPath,  // 회사별 출퇴근 시간 설정 API 경로 (반차 기준시간 참조용)
  settingsApiPathBuilder,
  hideSheetSync = false,  // 통합 모드 (Phase 1-1d) — 구글시트 동기화 버튼 숨김
  hideEventTypeSettings = false,  // 통합 모드 (2026-10-06) — '유형 설정' 숨김(템플릿은 회사마다 따로, 각 회사 화면에서 고침)
  showBulk = false,
  // 통합 모드 전용 (Phase 1-1e) — 회사 필터 칩. 3개 모두 전달되어야 렌더됨. 파인/ext는 미전달.
  companyCodes,
  selectedCompanyCodes,
  setSelectedCompanyCodes,
}) {
  const [eventTypeOpen, setEventTypeOpen] = useState(false);
  // ── 메인 상태·핸들러 훅 ────────────────────────────────────────────────────
  const state = useLeaveManagement({ apiBase, syncLeavesEndpoint, settingsApiPath, settingsApiPathBuilder, selectedCompanyCodes });

  // ── 파생 데이터 훅 ─────────────────────────────────────────────────────────
  const derived = useLeaveDerived(state);

  // ── 대량 붙여넣기 핸들러 훅 ───────────────────────────────────────────────
  const bulk = useBulkHandlers(state, derived, apiBase);

  // totalPages 변경 시 currentPage 범위 보정
  useEffect(() => {
    state.setCurrentPage((prev) => Math.min(Math.max(prev, 1), derived.totalPages));
  }, [derived.totalPages]); // eslint-disable-line

  const {
    isMobile,
    summaryRows, eventTypes, includeResignedSummary, setIncludeResignedSummary,
    nameQuery, setNameQuery,
    activeLeaveFilters, setActiveLeaveFilters,
    sheetUrl, syncGoogleSheet,
    currentPage, setCurrentPage,
    open, setOpen, editId,
    form, updateFormWithRules,
    userPickerText, handleUserPickerChange,
    selectedDates, setSelectedDates,
    selectedUserIds, setSelectedUserIds,
    showResignedInPicker, setShowResignedInPicker,
    toggleUserSelection,
    bulkOpen, setBulkOpen,
    bulkRows, bulkSubmitting,
    openCreate, openEdit, save, remove,
  } = state;

  const formEventTypes = useMemo(
    () => eventTypesForForm(eventTypes, state.users, form, selectedUserIds),
    [eventTypes, state.users, form, selectedUserIds],
  );
  // 통합(회사 여럿)에서 직원을 바꿔 고른 템플릿이 목록에서 빠지면 템플릿 표시만 지운다(채워진 값은 그대로 — '직접 설정').
  //   회사 하나인 화면은 손대지 않는다 — 없어진 옛 템플릿이 붙은 기록을 고칠 때 번호가 지워지면 안 된다.
  const multiCompanyTypes = new Set((eventTypes || []).map((t) => String(t.company_code || '').toUpperCase())).size > 1;
  const staleEventType = multiCompanyTypes && Boolean(form?.event_type_id)
    && !formEventTypes.some((t) => String(t.id) === String(form.event_type_id));
  useEffect(() => {
    if (open && staleEventType) updateFormWithRules({ event_type_id: '', event_name: '' }, { notify: false });
  }, [open, staleEventType]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    filteredList, pagedList, totalPages,
    lockedCategory, categoryOptions, pickerUsers,
    halfLeaveThresholdWarning, multiUserHalfLeaveWarning,
    bulkErrors, bulkNonEmptyCount, bulkInvalidRowCount,
  } = derived;

  return (
    <div style={pageWrapStyle}>

      {/* ── 페이지 헤더 (모바일: sticky — 아이콘 + 이름 검색 동시 고정 / 데스크탑: 표준 텍스트 헤더) ─── */}
      {isMobile ? (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg-card)', padding: '8px 10px', borderBottom: '1px solid var(--border)', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
          <div style={pageHeaderInner()}>
            {/* 좌: 제목 + 회사칩 — flex:1 + minWidth:0로 좁아지면 회사칩 wrap (아이콘은 항상 같은 줄) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              <h2 style={pageTitleStyle}>{pageTitle}</h2>
              {Array.isArray(companyCodes) && companyCodes.length > 0 && setSelectedCompanyCodes && (
                <CompanyFilterChips
                  companyCodes={companyCodes}
                  selected={selectedCompanyCodes || companyCodes}
                  onChange={setSelectedCompanyCodes}
                />
              )}
            </div>
            {/* 헤더 액션 — 옵션 C (모바일: 아이콘만 정사각) */}
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center', flexShrink: 0 }}>
              <HeaderActionBtn isMobile={isMobile} onClick={() => setIncludeResignedSummary(!includeResignedSummary)}
                icon={includeResignedSummary ? <User size={20} /> : <Users size={20} />}
                text={includeResignedSummary ? '전체' : '재직중'}
                active={!includeResignedSummary} title={includeResignedSummary ? '전체(퇴사자 포함)' : '재직중'} />
              <HeaderActionBtn isMobile={isMobile} onClick={openCreate}
                icon={<Plus size={20} />} text="연차 추가" active title="연차 추가" />
              {!hideEventTypeSettings && (
                <HeaderActionBtn isMobile={isMobile} onClick={() => setEventTypeOpen(true)}
                  icon={<Settings size={20} />} text="유형 설정" title="휴가/근태 유형 설정" />
              )}
            </div>
          </div>
          {/* 모바일: 이름 검색 — 폭 축소 (max 200px), 좌측 정렬 — 우측 여백 확보 */}
          <div style={{ marginTop: 6 }}>
            <input
              style={{ ...inputStyle, maxWidth: 200, width: '100%' }}
              value={nameQuery}
              placeholder="이름 검색"
              onChange={(e) => setNameQuery(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div style={stdHeaderRow(false)}>
          {/* 좌: 제목 + 회사칩 + 부제 (직원 관리 기준 텍스트 헤더) */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={stdPageTitle}>{pageTitle}</h2>
              {Array.isArray(companyCodes) && companyCodes.length > 0 && setSelectedCompanyCodes && (
                <CompanyFilterChips
                  companyCodes={companyCodes}
                  selected={selectedCompanyCodes || companyCodes}
                  onChange={setSelectedCompanyCodes}
                />
              )}
            </div>
            <div style={stdPageSubtitle}>직원 연차 부여·사용을 관리합니다.</div>
          </div>
          {/* 우: 재직중/전체 알약 토글 + 보조(유형 설정) + 주요(연차 추가) */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            <div style={stdTabGroup}>
              <button style={stdTabBtn(!includeResignedSummary)} onClick={() => setIncludeResignedSummary(false)}>재직중</button>
              <button style={stdTabBtn(includeResignedSummary)} onClick={() => setIncludeResignedSummary(true)}>전체(퇴사자 포함)</button>
            </div>
            {!hideEventTypeSettings && (
              <button style={stdOutlineBtn} onClick={() => setEventTypeOpen(true)} title="휴가/근태 유형 설정">유형 설정</button>
            )}
            <button style={stdBlackBtn} onClick={openCreate}>+ 연차 추가</button>
          </div>
        </div>
      )}

      {/* ── 연차 요약 섹션 — 모바일에서 아코디언 펼침 시 해당 직원 연차 내역 포함 ─── */}
      <SummarySection
        summaryRows={summaryRows}
        isMobile={isMobile}
        selectedName={nameQuery}
        onRowClick={(name) => setNameQuery(name)}
        activeTypeFilters={activeLeaveFilters}
        onToggleTypeFilter={(key) => setActiveLeaveFilters((prev) =>
          prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        )}
        leaves={filteredList}
        onEditLeave={openEdit}
      />

      {/* ── 연차 내역 섹션 — 데스크탑만 (모바일은 SummarySection 아코디언이 포함) ─── */}
      {!isMobile && <DetailSection
        isMobile={isMobile}
        nameQuery={nameQuery}
        setNameQuery={setNameQuery}
        filteredList={filteredList}
        pagedList={pagedList}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        sheetUrl={sheetUrl}
        syncGoogleSheet={syncGoogleSheet}
        setBulkOpen={setBulkOpen}
        openCreate={openCreate}
        openEdit={openEdit}
        remove={remove}
        hideSheetSync={hideSheetSync}
        showBulk={showBulk}
      />}

      {/* ── 연차 추가/수정 모달 ───────────────────────────────────────────── */}
      <AddEditModal
        open={open}
        onClose={() => setOpen(false)}
        editId={editId}
        save={save}
        onDelete={editId ? async () => { await remove({ id: editId }); setOpen(false); } : undefined}
        form={form}
        updateFormWithRules={updateFormWithRules}
        userPickerText={userPickerText}
        handleUserPickerChange={handleUserPickerChange}
        pickerUsers={pickerUsers}
        selectedUserIds={selectedUserIds}
        setSelectedUserIds={setSelectedUserIds}
        selectedDates={selectedDates}
        setSelectedDates={setSelectedDates}
        showResignedInPicker={showResignedInPicker}
        setShowResignedInPicker={setShowResignedInPicker}
        toggleUserSelection={toggleUserSelection}
        lockedCategory={lockedCategory}
        categoryOptions={categoryOptions}
        halfLeaveThresholdWarning={halfLeaveThresholdWarning}
        multiUserHalfLeaveWarning={multiUserHalfLeaveWarning}
        eventTypes={formEventTypes}
      />

      {/* ── 대량 추가 모달 ────────────────────────────────────────────────── */}
      {showBulk && (
        <BulkModal
          bulkOpen={bulkOpen}
          closeBulkModal={bulk.closeBulkModal}
          bulkRows={bulkRows}
          bulkErrors={bulkErrors}
          bulkNonEmptyCount={bulkNonEmptyCount}
          bulkInvalidRowCount={bulkInvalidRowCount}
          bulkSubmitting={bulkSubmitting}
          addBulkRows={bulk.addBulkRows}
          trimEmptyBulkRows={bulk.trimEmptyBulkRows}
          handleBulkCellChange={bulk.handleBulkCellChange}
          handleBulkCellPaste={bulk.handleBulkCellPaste}
          submitBulk={bulk.submitBulk}
        />
      )}

      <EventTypeSettingsModal
        open={eventTypeOpen && !hideEventTypeSettings}
        onClose={() => setEventTypeOpen(false)}
        apiBase={apiBase}
        eventTypes={eventTypes}
        onChanged={state.fetchAll}
      />
    </div>
  );
}

export default LeaveManagement;
